import type { JSX } from "react";
import { useEffect } from "react";
import { PROJECTS } from "@/ui/MonitorContent/Projects/data/projects";
import { useSceneStore } from "@/state/useSceneStore";

const isTypingTarget = (t: EventTarget | null): boolean => {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || t.isContentEditable;
};

interface Props {
  slug: string;
}

export const CaseStudy = ({ slug }: Props): JSX.Element => {
  const project = PROJECTS.find((p) => p.slug === slug);
  const setRoute = useSceneStore((s) => s.setActiveMonitorRoute);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.key === "Escape") {
        setRoute("#/projects");
        return;
      }
      if (e.key === "j" || e.key === "k") {
        const i = PROJECTS.findIndex((p) => p.slug === slug);
        if (i < 0) return;
        const len = PROJECTS.length;
        const next =
          e.key === "j"
            ? PROJECTS[(i + 1) % len]
            : PROJECTS[(i - 1 + len) % len];
        if (next) setRoute(`#/projects/${next.slug}`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slug, setRoute]);

  if (!project) {
    return (
      <p className="font-sans text-ink1">
        Project not found.{" "}
        <button
          type="button"
          onClick={() => setRoute("#/projects")}
          className="text-phosphor underline"
        >
          back
        </button>
      </p>
    );
  }

  return (
    <article className="max-w-[70ch] font-sans">
      <header>
        <h1 className="font-display text-4xl">{project.title}</h1>
        <div className="mt-2 flex gap-3 font-mono text-xs uppercase text-ink2">
          <span>{project.year}</span>
          <span>{project.role}</span>
          <span className="border border-rule px-1.5">{project.status}</span>
        </div>
        <ul className="mt-3 flex flex-wrap gap-1 font-mono text-[10px] uppercase">
          {project.tags.map((t) => (
            <li key={t} className="border border-rule px-1.5">
              {t}
            </li>
          ))}
        </ul>
      </header>
      <p className="mt-6 text-ink1">{project.summary}</p>
      {project.caseStudy && (
        <>
          <section className="mt-8 grid gap-4 md:grid-cols-3">
            <div>
              <div className="font-mono text-xs uppercase text-ink2">
                problem
              </div>
              <p className="mt-1 text-sm">{project.caseStudy.problem}</p>
            </div>
            <div>
              <div className="font-mono text-xs uppercase text-ink2">
                my role
              </div>
              <p className="mt-1 text-sm">{project.caseStudy.role}</p>
            </div>
            <div>
              <div className="font-mono text-xs uppercase text-ink2">stack</div>
              <p className="mt-1 text-sm">
                {project.caseStudy.stack.join(" · ")}
              </p>
            </div>
          </section>
          <section className="mt-8 space-y-3">
            <div className="font-mono text-xs uppercase text-ink2">process</div>
            <ol className="space-y-3">
              {project.caseStudy.process.map((p, i) => (
                <li key={i} className="border border-rule p-3">
                  <div className="font-display text-lg">
                    {i + 1}. {p.title}
                  </div>
                  <p className="mt-1 text-sm text-ink1">{p.body}</p>
                </li>
              ))}
            </ol>
          </section>
          <section className="mt-8">
            <div className="font-mono text-xs uppercase text-ink2">outcome</div>
            <p className="mt-1 text-sm">{project.caseStudy.outcome}</p>
          </section>
        </>
      )}
      <footer className="mt-10 flex gap-3 font-mono text-xs uppercase">
        {project.liveUrl && (
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-rule px-3 py-1 hover:border-phosphor"
          >
            live →
          </a>
        )}
        {project.repoUrl && (
          <a
            href={project.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-rule px-3 py-1 hover:border-phosphor"
          >
            repo →
          </a>
        )}
        <button
          type="button"
          onClick={() => setRoute("#/projects")}
          className="border border-rule px-3 py-1 hover:border-phosphor"
        >
          ← back
        </button>
      </footer>
    </article>
  );
};
